import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../routes/paths';
import { convertToVwMob } from '../Utils/responsive';
import mainLogo from '../assets/Logo/main_logo.png';
import {
  MISSIONFUND_PRIVACY_POLICY_URL,
  MISSIONFUND_TERMS_OF_SERVICE_URL,
  MISSIONFUND_SUPPORT_URL,
} from '../Utils/missionFundLegalUrls';
import kakaoLogo from '../assets/Icon/footer/kakao_icon.png';
import callIcon from '../assets/Icon/footer/famicons_call-sharp.svg';
import emailIcon from '../assets/Icon/footer/ic_round-email.svg';

const KAKAO_CHANNEL_CHAT_URL = 'http://pf.kakao.com/_xkHknxd/chat';

type MobileFooterProps = { showBottomNav?: boolean };

const MobileFooter = React.forwardRef<HTMLElement, MobileFooterProps>(({ showBottomNav = false }, ref) => {
  const { i18n, t } = useTranslation('translation');
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'ko').split('-')[0];
  const isKorean = currentLang === 'ko';
  const handleKakaoChannelInquiry = () => {
    window.open(KAKAO_CHANNEL_CHAT_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Wrapper ref={ref} $showBottomNav={showBottomNav}>
      <Inner>
        <Logo src={mainLogo} alt={t('common.footer.logoAlt')} />

        <Details>
          <div>{t('common.footer.brandLine')}</div>
          <div>{t('common.footer.addressKorea')}</div>
          <ContactGroup>
            <ContactLine>
              <ContactIcon src={callIcon} alt="" aria-hidden="true" />
              <span>1899-0693</span>
            </ContactLine>
            <ContactLine>
              <ContactIcon src={emailIcon} alt="" aria-hidden="true" />
              <span>cs@missionfund.org</span>
            </ContactLine>
          </ContactGroup>
          <div>{t('common.footer.addressUS')}</div>
        </Details>

        <Section>
          <SectionTitle>{t('common.footer.servicesHeading')}</SectionTitle>
          <SectionNavLink to={PATHS.NEW_PROJECT}>{t('common.footer.createProject')}</SectionNavLink>
          <SectionExternalLink href={MISSIONFUND_SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            {t('common.footer.consultingApply')}
          </SectionExternalLink>
        </Section>

        <Section>
          <SectionTitle>{t('common.footer.privacyTerms')}</SectionTitle>
          {isKorean ? (
            <>
              <SectionPolicyLink
                href={MISSIONFUND_PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.privacyPolicy')}
              </SectionPolicyLink>
              <SectionPolicyLink
                href={MISSIONFUND_TERMS_OF_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.servicePolicy')}
              </SectionPolicyLink>
            </>
          ) : (
            <>
              <SectionPolicyLink
                href={MISSIONFUND_TERMS_OF_SERVICE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.servicePolicy')}
              </SectionPolicyLink>
              <SectionPolicyLink
                href={MISSIONFUND_PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('common.footer.privacyPolicy')}
              </SectionPolicyLink>
            </>
          )}
        </Section>

        <KakaoButton type="button" onClick={handleKakaoChannelInquiry}>
          <KakaoDot src={kakaoLogo} alt="Kakao" />
          {t('common.footer.kakaoChannelInquiry')}
        </KakaoButton>

        <Divider />

        <Statement>
          {t('common.footer.statementKo')}
          <br />
          {t('common.footer.statementEn')}
        </Statement>

        <LangSelect
          aria-label="language"
          value={currentLang}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
        >
          <option value="ko">{t('translation:lang.ko')}</option>
          <option value="en">{t('translation:lang.en')}</option>
        </LangSelect>
      </Inner>
    </Wrapper>
  );
});

const BOTTOM_NAV_HEIGHT = 104;

const Wrapper = styled.footer<{ $showBottomNav?: boolean }>`
  background: #EEF3F9;
  padding: ${convertToVwMob(36)} ${convertToVwMob(22)} ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
`;

const Inner = styled.div`
  margin: 0 auto;
  max-width: ${convertToVwMob(350)};
  display: flex;
  flex-direction: column;
  gap: 0;
  align-items: center;
  text-align: center;
`;

const Logo = styled.img`
  height: ${convertToVwMob(38)};
  object-fit: contain;
  margin-bottom: ${convertToVwMob(24)};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(5)};
  font-size: ${convertToVwMob(14)};
  line-height: 1.3;
  color: ${({ theme }) => theme.colors.grayScale[70]};
  margin-bottom: ${convertToVwMob(44)};
`;

const ContactGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(8)};
  flex-wrap: wrap;
`;

const ContactLine = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(4)};
`;

const ContactIcon = styled.img`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  flex: 0 0 ${convertToVwMob(12)};
  display: inline-block;
  object-fit: contain;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
  align-items: center;
  margin-bottom: ${convertToVwMob(28)};

  & + & {
    margin-bottom: ${convertToVwMob(44)};
  }
`;

const SectionTitle = styled.div`
  font-weight: 700; /* Caption/Bold16pt */
  font-size: ${convertToVwMob(16)};
  line-height: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[70]}; /* GreyScale/70 */
  margin-bottom: ${convertToVwMob(8)};
`;

const sectionServiceLinkStyles = `
  font-weight: 500;
  font-size: ${convertToVwMob(16)};
  line-height: ${convertToVwMob(20)};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionNavLink = styled(Link)`
  color: ${({ theme }) => theme.colors.grayScale[60]};
  ${sectionServiceLinkStyles}

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }
`;

const SectionExternalLink = styled.a`
  color: ${({ theme }) => theme.colors.grayScale[60]};
  ${sectionServiceLinkStyles}

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
  }
`;

const SectionPolicyLink = styled.a`
  font-weight: 500;
  font-size: ${convertToVwMob(16)};
  line-height: ${convertToVwMob(20)};
  color: ${({ theme }) => theme.colors.grayScale[60]};
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.grayScale[70]};
    text-decoration: underline;
  }
`;

const Statement = styled.div`
  font-size: ${convertToVwMob(12)};
  color: ${({ theme }) => theme.colors.grayScale[70]};
  line-height: 1.3;
  margin-bottom: ${convertToVwMob(44)};
`;

const Divider = styled.hr`
  width: 100%;
  border: none;
  border-top: 0.3px solid ${({ theme }) => theme.colors.grayScale[70]};
  margin: 0 0 ${convertToVwMob(44)};
`;

const LangSelect = styled.select`
  height: ${convertToVwMob(34)};
  min-width: ${convertToVwMob(96)};
  padding: 0 ${convertToVwMob(34)} 0 ${convertToVwMob(16)};
  border-radius: ${convertToVwMob(6)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[40]};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.grayScale[80]};
  font-size: ${convertToVwMob(12)};
  line-height: ${convertToVwMob(34)};
  cursor: pointer;

  /* match design: custom caret */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M7 10L12 15L17 10' stroke='%23929292' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${convertToVwMob(12)} center;
  background-size: ${convertToVwMob(16)} ${convertToVwMob(16)};
`;

const KakaoButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${convertToVwMob(10)};
  width: ${convertToVwMob(186)};
  height: ${convertToVwMob(36)};
  padding: 0 ${convertToVwMob(14)};
  border-radius: ${convertToVwMob(18)};
  border: 1px solid ${({ theme }) => theme.colors.grayScale[60]};
  background: transparent; /* Fill 없음: 푸터 배경색 노출 */
  color: ${({ theme }) => theme.colors.grayScale[80]};
  font-size: ${convertToVwMob(13)};
  font-weight: 700;
  font-family: Pretendard;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: ${convertToVwMob(52)};
`;

const KakaoDot = styled.img`
  width: ${convertToVwMob(18)};
  height: ${convertToVwMob(18)};
`;

export default MobileFooter;


